import React from 'react';
import type { HorsePerformance } from '../../../../types/horseProfile';

interface PerformanceSectionProps {
  performance: HorsePerformance;
}

const DEFAULT_PERFORMANCE: HorsePerformance = {
  trainingLevel: '',
  disciplines: [],
  competitions: [],
  goals: [],
  trainer: '',
  trainingSchedule: ''
};

export const PerformanceSection: React.FC<PerformanceSectionProps> = ({ performance = DEFAULT_PERFORMANCE }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Ausbildungsstand</h4>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">{performance.trainingLevel}</p>
          </div>
        </div>

        {performance.disciplines?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Disziplinen</h4>
            <div className="flex flex-wrap gap-2">
              {performance.disciplines.map((discipline, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
                >
                  {discipline}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {performance.competitions?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Turniererfolge</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Turnier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Disziplin
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Klasse
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ergebnis
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {performance.competitions.map((competition, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {new Date(competition.date).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {competition.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {competition.discipline}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {competition.level}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {competition.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {performance.goals?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Ziele</h4>
          <ul className="list-disc list-inside space-y-1">
            {performance.goals.map((goal, index) => (
              <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                {goal}
              </li>
            ))}
          </ul>
        </div>
      )}

      {performance.trainer && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Trainer</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300">{performance.trainer}</p>
        </div>
      )}

      {performance.trainingSchedule && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Trainingsplan</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
            {performance.trainingSchedule}
          </p>
        </div>
      )}
    </div>
  );
};